import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Fade,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachIcon
} from '@mui/icons-material';
import MessageBubble from './MessageBubble';
import { useTranslation } from 'react-i18next';
import miroAvatar from '../../assets/miro-avatar.png';

const ENGAGING_TIPS = [
  "Tip: Ask me to 'Rank candidates for Python Senior Dev' for instant AI semantic scoring!",
  "Tip: You can upload a candidate resume directly in chat for instant skill extraction!",
  "Tip: Ask me to 'Export report for Java developers' to download a clean spreadsheet!",
  "Tip: Need to update a job description? Just paste the new requirements here!",
  "Tip: I remember our conversation history so we can chat naturally across multiple questions!"
];

const WORKFLOW_STATES = [
  "⚡ Connecting to candidate database & verifying profile...",
  "🔍 Scanning candidate talent pool & skill matrices...",
  "🎯 Evaluating candidate qualifications against job requirements...",
  "🧠 Synthesizing tailored recruitment insights..."
];

const InteractiveThinkingBanner = () => {
  const [stateIdx, setStateIdx] = useState(0);
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    const stateTimer = setInterval(() => {
      setStateIdx((prev) => (prev + 1) % WORKFLOW_STATES.length);
    }, 1800);
    return () => clearInterval(stateTimer);
  }, []);

  const nextTip = () => {
    setTipIdx((prev) => (prev + 1) % ENGAGING_TIPS.length);
  };

  return (
    <Fade in timeout={300}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, mb: 2 }}>
        <Avatar
          src={miroAvatar}
          sx={{
            width: 38,
            height: 38,
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            border: '2px solid white'
          }}
        />
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 4,
            borderTopLeftRadius: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(243, 246, 255, 0.95) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(102, 126, 234, 0.25)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
            maxWidth: '85%',
            width: '100%',
            transition: 'all 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <CircularProgress size={18} sx={{ color: '#667eea' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#3b4151', minHeight: 20 }}>
              {WORKFLOW_STATES[stateIdx]}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: 'rgba(102, 126, 234, 0.08)',
              borderLeft: '3px solid #667eea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Typography variant="body2" sx={{ color: '#4a5568', fontStyle: 'italic', fontSize: '0.85rem' }}>
              💡 {ENGAGING_TIPS[tipIdx]}
            </Typography>
            <IconButton
              size="small"
              onClick={nextTip}
              title="Next Tip"
              sx={{
                color: '#667eea',
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { bgcolor: '#ffffff', transform: 'scale(1.05)' },
                transition: 'all 0.2s',
                borderRadius: 1.5,
                px: 1,
                py: 0.5
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>✨ Next Tip</Typography>
            </IconButton>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

const ChatInterface = ({ 
  messages, 
  isLoading, 
  onActionClick, 
  availableActions = [],
  onSendMessage,
  onFileSelect,
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { t, i18n } = useTranslation();

  // Debug logging
  React.useEffect(() => {
    console.log('ChatInterface - messages:', messages);
    console.log('ChatInterface - isLoading:', isLoading);
  }, [messages, isLoading]);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (inputValue.trim() && onSendMessage) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 2,
          minHeight: 0, // Important for flex scrolling
          background: 'linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3,
              textAlign: 'center',
            }}
          >
            <Box sx={{ position: 'relative', mb: 2.5 }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  left: -6,
                  right: -6,
                  bottom: -6,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)',
                  opacity: 0.25,
                  filter: 'blur(10px)',
                }}
              />
              <Avatar
                src={miroAvatar}
                sx={{
                  width: 80,
                  height: 80,
                  border: '3px solid white',
                  boxShadow: '0 8px 24px rgba(14, 165, 233, 0.25)',
                  position: 'relative',
                }}
              />
            </Box>

            <Typography variant="h6" fontWeight="700" sx={{ color: '#1e293b', mb: 0.8 }}>
               {t('chatbot.empty.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 360, mb: 3, lineHeight: 1.6 }}>
               {t('chatbot.empty.subtitle')}
            </Typography>

            {/* Quick Starter Pills */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', maxWidth: 440 }}>
              {[
                "Rank candidates for Python Dev",
                "Summarize recent uploads",
                "Create a new candidate profile"
              ].map((starter, i) => (
                <Chip
                  key={i}
                  label={starter}
                  onClick={() => onSendMessage && onSendMessage(starter)}
                  sx={{
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    color: '#334155',
                    fontWeight: 500,
                    py: 1.8,
                    px: 0.5,
                    borderRadius: '20px',
                    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.04)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#f0f9ff',
                      borderColor: '#0ea5e9',
                      color: '#0284c7',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(14, 165, 233, 0.15)',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((message, index) => (
            <Fade in timeout={300} key={`${message.timestamp}-${index}`}>
              <Box sx={{ mb: 2 }}>
                <MessageBubble
                  message={message}
                  onActionClick={onActionClick}
                />
              </Box>
            </Fade>
          ))
        )}
        
        {isLoading && <InteractiveThinkingBanner />}
        
        <div ref={messagesEndRef} />
      </Box>

      {/* Quick Actions */}
      {/* {availableActions.length > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableActions.slice(0, 4).map((action, index) => (
              <Chip
                key={index}
                label={action}
                onClick={() => onActionClick && onActionClick(action)}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0284c7 0%, #2563eb 100%)',
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      )} */}

      {/* Input Area */}
      <Box sx={{ p: 1.8, borderTop: '1px solid rgba(226, 232, 240, 0.8)', background: '#ffffff' }}>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
          {/* Compact Attachment Icon Button (In same line!) */}
          <input
            type="file"
            id="file-upload"
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              if (e.target.files && e.target.files[0] && onFileSelect) {
                onFileSelect(e.target.files[0]);
                e.target.value = ''; // Reset input
              }
            }}
            style={{ display: 'none' }}
          />
          <label htmlFor="file-upload" style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('chatbot.uploadResume') || "Attach file or resume"}>
              <IconButton
                component="span"
                sx={{
                  color: '#0ea5e9',
                  bgcolor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#e0f2fe',
                    color: '#0284c7',
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <AttachIcon />
              </IconButton>
            </Tooltip>
          </label>

          {/* Message Input */}
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder={t('chatbot.inputPlaceholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            inputRef={inputRef}
            autoFocus={messages.length === 0}
            slotProps={{
              input : {
                dir:i18n.dir()
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                bgcolor: '#f8fafc',
                px: 2,
                py: 1.2,
                fontSize: '0.95rem',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                },
                '&.Mui-focused': {
                  bgcolor: '#ffffff',
                  boxShadow: '0 0 0 2px rgba(14, 165, 233, 0.25)',
                },
                '& fieldset': {
                  borderColor: 'rgba(226, 232, 240, 0.9)',
                },
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            sx={{
              bgcolor: '#0ea5e9',
              color: 'white',
              width: 44,
              height: 44,
              borderRadius: '50%',
              boxShadow: '0 4px 12px rgba(14, 165, 233, 0.35)',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#0284c7',
                transform: 'scale(1.05)',
                boxShadow: '0 6px 16px rgba(14, 165, 233, 0.45)',
              },
              '&:disabled': {
                bgcolor: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none',
                transform: 'none',
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInterface;